import { useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Upload, CheckCircle2, AlertCircle, FileSpreadsheet, Trash2, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";

const TEMPLATE_URL = "/plantilla_casas_tracking.xlsx";

const SHEETS = [
    { key: "autos", label: "Autos" },
    { key: "categorias", label: "Categorías" },
    { key: "proveedores", label: "Proveedores" },
    { key: "movimientos", label: "Movimientos" },
    { key: "seguros", label: "Seguros" },
    { key: "verificaciones", label: "Verificaciones" },
    { key: "recordatorios", label: "Recordatorios" },
];

export default function ImportPage() {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);
    const inputRef = useRef(null);
    const [wipeOpen, setWipeOpen] = useState(false);
    const [wipeConfirm, setWipeConfirm] = useState("");
    const [wiping, setWiping] = useState(false);

    const wipe = async () => {
        if (wipeConfirm !== "BORRAR-TODO") {
            return toast.error("Escribe BORRAR-TODO para confirmar");
        }
        setWiping(true);
        try {
            const { data } = await api.post("/admin/wipe", null, { params: { confirm: "BORRAR-TODO" } });
            const totals = Object.values(data.deleted || {}).reduce((a, b) => a + (b || 0), 0);
            toast.success(`Se eliminaron ${totals} registros. Inicio desde cero listo.`);
            setWipeOpen(false);
            setWipeConfirm("");
            setResult(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al borrar");
        } finally {
            setWiping(false);
        }
    };

    const reseed = async () => {
        if (!window.confirm("Esto borra todo y vuelve a cargar el ejemplo. ¿Continuar?")) return;
        try {
            await api.post("/admin/reseed");
            toast.success("Datos de ejemplo recargados");
            setResult(null);
        } catch {
            toast.error("Error al recargar");
        }
    };

    const submit = async () => {
        if (!file) return toast.error("Selecciona el archivo Excel");
        const fd = new FormData();
        fd.append("file", file);
        setUploading(true);
        setResult(null);
        try {
            const { data } = await api.post("/import/xlsx", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(data);
            toast.success("Importación completada");
            setFile(null);
            if (inputRef.current) inputRef.current.value = "";
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al importar");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="import-page">
            <header>
                <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Carga masiva</p>
                <h1 className="text-3xl font-semibold tracking-tight text-slate-900 mt-1 font-display">
                    Importar desde Excel
                </h1>
                <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                    Descarga la plantilla, llénala con tus datos reales y súbela aquí. La app actualizará autos, categorías y proveedores existentes, y agregará movimientos, seguros y recordatorios nuevos.
                </p>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="border-slate-200 shadow-sm">
                    <CardHeader>
                        <CardTitle className="text-lg font-display font-medium flex items-center gap-2">
                            <FileSpreadsheet size={18} className="text-slate-500" />
                            1. Descarga la plantilla
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <p className="text-sm text-slate-600">
                            El archivo trae hojas para cada tipo de información, con ejemplos y validaciones de campos.
                        </p>
                        <a href={TEMPLATE_URL} download data-testid="download-template">
                            <Button className="bg-slate-900 hover:bg-slate-800 text-white">
                                <Download size={16} className="mr-2" /> plantilla_casas_tracking.xlsx
                            </Button>
                        </a>
                        <ul className="text-xs text-slate-500 mt-4 space-y-1 list-disc pl-5">
                            <li>Hojas: Autos · Categorías · Proveedores · Movimientos · Seguros · Recordatorios</li>
                            <li>Las filas con fondo gris son ejemplos — bórralas o reemplázalas.</li>
                            <li>No cambies los nombres de hojas ni encabezados.</li>
                            <li>Fechas en formato <span className="font-mono">YYYY-MM-DD</span>. Montos en MXN, solo números.</li>
                        </ul>
                    </CardContent>
                </Card>

                <Card className="border-slate-200 shadow-sm">
                    <CardHeader>
                        <CardTitle className="text-lg font-display font-medium flex items-center gap-2">
                            <Upload size={18} className="text-slate-500" />
                            2. Sube tu archivo lleno
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <input
                            ref={inputRef}
                            type="file"
                            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            className="block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-slate-900 file:text-white hover:file:bg-slate-800"
                            data-testid="import-file-input"
                        />
                        {file && <p className="text-xs text-slate-500">{file.name} · {(file.size / 1024).toFixed(0)} KB</p>}
                        <Button
                            onClick={submit}
                            disabled={!file || uploading}
                            className="bg-slate-900 hover:bg-slate-800 text-white"
                            data-testid="submit-import"
                        >
                            <Upload size={16} className="mr-2" />
                            {uploading ? "Procesando…" : "Importar datos"}
                        </Button>
                        <p className="text-xs text-slate-500 mt-2">
                            Tamaño máximo: 10 MB. Sólo archivos <span className="font-mono">.xlsx</span>.
                        </p>
                    </CardContent>
                </Card>
            </div>

            {result && (
                <Card className="border-slate-200 shadow-sm" data-testid="import-result">
                    <CardHeader>
                        <CardTitle className="text-lg font-display font-medium">Resultado</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {SHEETS.map(({ key, label }) => {
                                const r = result[key] || {};
                                const created = r.creados ?? r.creadas ?? 0;
                                const updated = r.actualizados ?? r.actualizadas;
                                const errs = (r.errores || []).length;
                                return (
                                    <div key={key} className="p-4 rounded-md border border-slate-200 bg-white">
                                        <div className="flex items-center justify-between">
                                            <div className="font-medium text-slate-900">{label}</div>
                                            {errs === 0 ? (
                                                <CheckCircle2 size={16} className="text-emerald-600" />
                                            ) : (
                                                <AlertCircle size={16} className="text-amber-600" />
                                            )}
                                        </div>
                                        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
                                            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{created} nuevos</Badge>
                                            {updated !== undefined && (
                                                <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">{updated} actualizados</Badge>
                                            )}
                                            {errs > 0 && (
                                                <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">{errs} errores</Badge>
                                            )}
                                        </div>
                                        {errs > 0 && (
                                            <ul className="mt-3 text-xs text-rose-600 space-y-1 max-h-32 overflow-y-auto">
                                                {r.errores.slice(0, 10).map((er) => (
                                                    <li key={`${key}-${er.fila}-${er.msg}`}>Fila {er.fila}: {er.msg}</li>
                                                ))}
                                                {r.errores.length > 10 && (
                                                    <li className="text-slate-500">… y {r.errores.length - 10} más</li>
                                                )}
                                            </ul>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            )}

            <Card className="border-rose-200 bg-rose-50/40 shadow-sm" data-testid="danger-zone">
                <CardHeader>
                    <CardTitle className="text-lg font-display font-medium text-rose-900 flex items-center gap-2">
                        <Trash2 size={18} /> Zona peligrosa
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <p className="text-sm text-rose-900/80 max-w-2xl">
                        Esta acción elimina <span className="font-semibold">absolutamente todos los datos</span> (autos, movimientos, categorías, proveedores, seguros, verificaciones, recordatorios, expedientes y recibos). Es irreversible y deja la app vacía para que puedas empezar desde cero.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant="outline"
                            className="border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
                            onClick={() => setWipeOpen(true)}
                            data-testid="open-wipe"
                        >
                            <Trash2 size={14} className="mr-1" /> Borrar todos los datos
                        </Button>
                        <Button
                            variant="outline"
                            className="border-slate-200 bg-white"
                            onClick={reseed}
                            data-testid="reseed-button"
                        >
                            <RefreshCw size={14} className="mr-1" /> Recargar datos de ejemplo
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <Dialog open={wipeOpen} onOpenChange={(v) => { setWipeOpen(v); if (!v) setWipeConfirm(""); }}>
                <DialogContent data-testid="wipe-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-rose-900">¿Borrar todos los datos?</DialogTitle>
                        <DialogDescription>
                            Esta acción es permanente. Escribe exactamente <span className="font-mono font-semibold">BORRAR-TODO</span> para continuar.
                        </DialogDescription>
                    </DialogHeader>
                    <Input
                        value={wipeConfirm}
                        onChange={(e) => setWipeConfirm(e.target.value)}
                        placeholder="BORRAR-TODO"
                        className="font-mono"
                        data-testid="wipe-confirm-input"
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setWipeOpen(false)}>Cancelar</Button>
                        <Button
                            onClick={wipe}
                            disabled={wiping || wipeConfirm !== "BORRAR-TODO"}
                            className="bg-rose-600 hover:bg-rose-700 text-white"
                            data-testid="confirm-wipe"
                        >
                            {wiping ? "Borrando…" : "Sí, borrar todo"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
